import type { RoleView } from '../../lib/api';

/**
 * How a role is NAMED and GROUPED in the invite picker.
 *
 * ── WHY THIS IS NOT JUST `role.name` ────────────────────────────────
 * The seeded Role.name values are our vocabulary, and two of them collided
 * one line apart in the dropdown: "Fleet Operator · system" sat directly
 * above "Operator · system". Those are unrelated populations — a customer who
 * runs a vehicle fleet, and our own back-office staff — and the only thing
 * distinguishing them was a word the inviting admin has no reason to know.
 *
 * Only the two that are actually wrong are renamed. "Tenant Admin",
 * "Operator" and "Viewer" keep their names: they are internal tiers whose
 * words the product already uses consistently, and re-coining them here would
 * put a second vocabulary in front of the same people. Custom tenant clones
 * always keep the name their author gave them.
 */
export const ROLE_DISPLAY_NAME: Record<string, string> = {
  /** Warren's own word, and the one nav.ts uses throughout. Kills the
   *  collision with "Operator". */
  fleet_admin: 'Fleet admin',
  /** The rest of the product says Sales Partner — the nav leaf is
   *  "Sales Partners". "Agent" is the codebase identifier, not the word. */
  sales_agent: 'Sales partner',
};

/** The rep role's name in the Admin UI (Warren, 20 October 2026). The Api
 *  seeds and stores the role as "Sales Rep" (template sales_rep); every
 *  screen that shows a role name shows this instead. */
export const SALES_REP_ROLE_LABEL = 'SP Manager';

/** A role name as the Admin UI shows it: "Sales Rep" reads "SP Manager",
 *  matched on the template where the caller has it, else on the stored name
 *  (list rows carry only the name). Every other name is unchanged. */
export function roleNameForDisplay(name: string | null | undefined, template?: string | null): string {
  if (template === 'sales_rep' || name?.trim().toLowerCase() === 'sales rep') return SALES_REP_ROLE_LABEL;
  return name ?? '';
}

/** The picker label for a role. System roles may be renamed above; a custom
 *  tenant clone always keeps its author's name. */
export function roleDisplayName(r: RoleView): string {
  // The sales rep role is a tenant role (not is_system), so it is named here, before the custom-name rule.
  if ((r.template as string | null) === 'sales_rep') return SALES_REP_ROLE_LABEL;
  if (!r.is_system) return r.name;
  return (r.system_role ? ROLE_DISPLAY_NAME[r.system_role] : undefined)
    ?? (r.template ? ROLE_DISPLAY_NAME[r.template] : undefined)
    ?? r.name;
}

interface Group { label: string; match: (r: RoleView) => boolean }

/** Groups exist so the populations are visibly separate rather than adjacent.
 *  Order is the order they render. */
export const ROLE_GROUPS: Group[] = [
  {
    label: 'Your team',
    match: (r) => r.is_system
      && ['tenant_admin', 'operator', 'viewer'].includes(r.system_role ?? ''),
  },
  {
    label: 'Site & property',
    match: (r) => r.is_system && r.system_role === 'third_party_admin'
      && ['building_admin', 'charger_owner', 'property_manager'].includes(r.template ?? ''),
  },
  {
    label: 'Fleet customers',
    match: (r) => r.is_system && r.system_role === 'fleet_admin',
  },
  {
    label: 'Partners',
    match: (r) => r.is_system && r.template === 'sales_agent',
  },
  { label: 'Custom roles', match: (r) => !r.is_system },
];

export interface GroupedRoles {
  groups: Array<{ label: string; roles: RoleView[] }>;
  /** Roles no group claimed. Rendered under "Other" — a new template must
   *  never become INVISIBLE in the one picker that assigns it. */
  ungrouped: RoleView[];
}

export function groupRoles(roles: RoleView[]): GroupedRoles {
  const claimed = new Set<string>();
  const groups = ROLE_GROUPS
    .map((g) => {
      const matched = roles.filter((r) => g.match(r));
      for (const r of matched) claimed.add(r._id);
      return { label: g.label, roles: matched };
    })
    .filter((g) => g.roles.length > 0);
  return { groups, ungrouped: roles.filter((r) => !claimed.has(r._id)) };
}
